import React, { useMemo, useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Layout } from '../components/layout';
import { CaseGrid, ScaleView } from '../components/cases';
import { FilterBar, FilterPanel, SortOption } from '../components/filters';
import { CompareDock } from '../components/comparison';
import { Button, SearchInput } from '../components/ui';
import { useAppStore } from '../context/store';
import { useCases, useCaseStats } from '../hooks/useCases';
import { useSEO } from '../hooks/useSEO';

const sortParams: Record<SortOption, { sortBy: 'name' | 'manufacturer' | 'releaseYear'; sortOrder: 'asc' | 'desc' }> = {
  'name-asc': { sortBy: 'name', sortOrder: 'asc' },
  'manufacturer-asc': { sortBy: 'manufacturer', sortOrder: 'asc' },
  'releaseYear-desc': { sortBy: 'releaseYear', sortOrder: 'desc' },
};

export const Home: React.FC = () => {
  useSEO({
    title: 'PC Case Database',
    description: 'Browse and compare PC cases from top manufacturers. Filter by size, form factor, GPU clearance, and more. Find the perfect case for your build.',
  });

  const { filters, comparison, setSearchQuery, addToComparison, removeFromComparison } = useAppStore();
  const [sort, setSort] = useState<SortOption>('name-asc');
  const unit = comparison.unitSystem === 'metric' ? 'mm' : 'in';

  const savedScrollRef = useRef(sessionStorage.getItem('home_scroll'));
  const savedPagesRef = useRef(parseInt(sessionStorage.getItem('home_pages') || '1'));
  const pageRef = useRef(1);
  const scrollPosRef = useRef(0);

  // Build query params from filters
  const queryParams = useMemo(() => ({
    search: filters.searchQuery || undefined,
    sizeCategories: filters.sizeCategories.length > 0 ? filters.sizeCategories : undefined,
    formFactors: filters.formFactors.length > 0 ? filters.formFactors : undefined,
    manufacturers: filters.manufacturers.length > 0 ? filters.manufacturers : undefined,
    sidePanelTypes: filters.sidePanelTypes.length > 0 ? filters.sidePanelTypes : undefined,
    minGpuLength: filters.minGpuLength > 0 ? filters.minGpuLength : undefined,
    minCoolerHeight: filters.minCoolerHeight > 0 ? filters.minCoolerHeight : undefined,
    minPsuLength: filters.minPsuLength > 0 ? filters.minPsuLength : undefined,
    minRadiatorSize: filters.minRadiatorSize > 0 ? filters.minRadiatorSize : undefined,
    ...sortParams[sort],
    pageSize: 24,
  }), [filters, sort]);

  // Fetch cases with filters
  const { cases: filteredCases, loading, totalCount, hasMore, loadMore, page } = useCases(queryParams);

  useEffect(() => { pageRef.current = page; }, [page]);

  // Track scroll position in memory — DOM is gone by the time cleanup runs
  useEffect(() => {
    const main = document.getElementById('main-content');
    if (!main) return;
    const onScroll = () => { scrollPosRef.current = main.scrollTop; };
    main.addEventListener('scroll', onScroll, { passive: true });
    return () => main.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-load pages when restoring
  useEffect(() => {
    if (savedScrollRef.current && !loading && page < savedPagesRef.current && hasMore) {
      loadMore();
    }
  }, [loading, page, hasMore, loadMore]);

  // Restore scroll after all saved pages are loaded
  useEffect(() => {
    if (!savedScrollRef.current) return;
    if (loading || (page < savedPagesRef.current && hasMore)) return;

    const main = document.getElementById('main-content');
    const pos = parseInt(savedScrollRef.current);
    if (main && pos > 0) {
      main.style.scrollBehavior = 'auto';
      main.scrollTop = pos;
    }
    sessionStorage.removeItem('home_scroll');
    sessionStorage.removeItem('home_pages');
    savedScrollRef.current = null;
  }, [loading, page, hasMore]);

  // Save on unmount — reads from ref (not DOM) since DOM is already swapped
  useEffect(() => {
    return () => {
      if (scrollPosRef.current > 0) {
        sessionStorage.setItem('home_scroll', String(scrollPosRef.current));
        sessionStorage.setItem('home_pages', String(pageRef.current));
      }
    };
  }, []);

  // Fetch global stats (total counts per size category)
  const { stats } = useCaseStats();

  const comparisonFull = comparison.selectedCases.length >= 4;

  const toggleComparison = (id: string) => {
    if (comparison.selectedCases.includes(id)) {
      removeFromComparison(id);
    } else {
      addToComparison(id);
    }
  };

  return (
    <Layout fullBleed>
      {/* Hero + scale view */}
      <section className="border-b border-line bg-blueprint">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 px-4 pt-12 pb-10 sm:px-6 sm:pt-14">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div className="flex max-w-2xl flex-col gap-3.5">
              <span className="font-mono text-xs uppercase tracking-label text-muted">
                PC case database{stats.total > 0 ? ` · ${stats.total} cases` : ''}
              </span>
              <h1 className="text-4xl font-semibold leading-[1.04] tracking-tightest sm:text-5xl">
                Every case, drawn to scale.
              </h1>
              <p className="text-base leading-relaxed text-muted sm:text-lg">
                Compare real dimensions, GPU clearance and cooler height side by side — before you buy.
              </p>
            </div>
            <div className="w-full sm:w-96">
              <SearchInput
                fieldSize="lg"
                aria-label="Search cases"
                placeholder="Search by brand or model…"
                value={filters.searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          <ScaleView
            cases={filteredCases}
            selectedIds={comparison.selectedCases}
            onToggle={toggleComparison}
            unit={unit}
            canAddMore={!comparisonFull}
          />
        </div>
      </section>

      <FilterBar stats={stats} sort={sort} onSortChange={setSort} />

      {/* Results */}
      <div className="mx-auto flex max-w-7xl flex-col gap-5 px-4 pt-7 pb-28 sm:px-6">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <h2 className="text-[15px] font-medium">
            <span className="font-mono">{totalCount}</span> {totalCount === 1 ? 'case' : 'cases'}
          </h2>
          {comparisonFull && (
            <span className="text-sm text-accent-ink">Comparison is full — remove a case to add another.</span>
          )}
        </div>

        {loading && filteredCases.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-7 w-7 animate-spin text-muted" aria-label="Loading cases" />
          </div>
        ) : (
          <>
            <CaseGrid cases={filteredCases} />

            {hasMore && (
              <div className="flex justify-center pt-4">
                <Button variant="outline" size="lg" onClick={loadMore} disabled={loading}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading…
                    </>
                  ) : (
                    `Load more · ${totalCount - filteredCases.length} left`
                  )}
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <CompareDock />
      <FilterPanel resultCount={totalCount} />
    </Layout>
  );
};
